import {
  type UnionAddress,
  getBlockchainFromAddress,
  getRawAddress,
} from "@anaconda/core/blockchain/address"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { getFeaturedBlockchain } from "../../blockchains/featured-blockchain/get-featured-blockchain"

const PROFILE_SUBROUTES = new Set(["listings", "offers", "activity"])

export function getProfileSiteBlockchain(
  walletAddress: UnionAddress,
  selectedBlockchain?: Blockchain,
): Blockchain {
  const walletBlockchain = getBlockchainFromAddress(walletAddress)

  if (walletBlockchain === Blockchain.SOLANA) {
    return Blockchain.SOLANA
  }

  if (walletBlockchain === Blockchain.ECLIPSE) {
    return Blockchain.ECLIPSE
  }

  // EVM wallet profiles must stay on an EVM site chain (e.g. /ethereum/...),
  // even when the user is browsing from /solana/... context.
  if (
    selectedBlockchain &&
    selectedBlockchain !== Blockchain.SOLANA &&
    selectedBlockchain !== Blockchain.ECLIPSE
  ) {
    return selectedBlockchain
  }

  return getFeaturedBlockchain()
}

export function buildProfileHrefPath(
  walletAddress: UnionAddress,
  selectedBlockchain?: Blockchain,
): string {
  const walletBlockchain = getBlockchainFromAddress(walletAddress).toLowerCase()
  const walletRawAddress = getRawAddress(walletAddress)
  const siteBlockchain = getProfileSiteBlockchain(
    walletAddress,
    selectedBlockchain,
  )

  return `/${siteBlockchain.toLowerCase()}/profiles/${walletBlockchain}/${walletRawAddress}`
}

/** Portfolio navigation prefers EVM wallet when both chains are linked. */
export function resolvePortfolioUnionAddress(
  evmUnion?: UnionAddress,
  solanaUnion?: UnionAddress,
): UnionAddress | undefined {
  return evmUnion ?? solanaUnion
}

/**
 * Solana wallet profiles use /solana/profiles/solana/{address} as the canonical URL.
 * Returns the canonical path when the current pathname uses a different site chain.
 */
export function getCanonicalSolanaProfilePath(pathname: string): string | null {
  const pathParts = pathname.split("/").filter(Boolean)

  if (pathParts.length < 4) {
    return null
  }

  if (pathParts[1] !== "profiles") {
    return null
  }

  if (pathParts[2].toUpperCase() !== Blockchain.SOLANA) {
    return null
  }

  if (pathParts[0].toUpperCase() === Blockchain.SOLANA) {
    return null
  }

  const address = pathParts[3]
  const subpath = pathParts
    .slice(4)
    .filter((segment) => PROFILE_SUBROUTES.has(segment))
  const suffix = subpath.length > 0 ? `/${subpath.join("/")}` : ""

  return `/solana/profiles/solana/${address}${suffix}`
}
